import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { statusLabel, statusStyle } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Users, Clock, CheckCircle2, LogIn, MessageSquare, Search, ClipboardList, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const dtf = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";
const hm = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";
const todayStr = () => new Date().toISOString().slice(0, 10);

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="rounded-2xl p-5 flex items-center gap-4" data-testid={testid}>
      <div className="h-12 w-12 rounded-xl grid place-items-center bg-primary/10 text-primary"><Icon className="h-6 w-6" aria-hidden="true" /></div>
      <div><div className="text-3xl font-heading font-bold tracking-tight">{value}</div><div className="text-sm text-muted-foreground">{label}</div></div>
    </Card>
  );
}

export default function Dashboard() {
  const confirm = useConfirm();
  const [data, setData] = useState({ requests: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [fDate, setFDate] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fDate) params.date = fDate;
      if (fStatus !== "all") params.status = fStatus;
      const { data: d } = await api.get("/requests", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [fDate, fStatus]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return data.requests;
    return data.requests.filter((r) =>
      `${r.patientFirstName || ""} ${r.patientLastName || ""}`.toLowerCase().includes(q) ||
      (r.publicCode || "").toLowerCase().includes(q));
  }, [data.requests, search]);

  const stats = useMemo(() => {
    const rs = data.requests;
    return {
      total: rs.length,
      waiting: rs.filter((r) => ["RECHERCHE_MEDICALE_EN_COURS", "RENDEZ_VOUS_MEDICAL_SELECTIONNE", "RECHERCHE_CABINE", "CABINE_EN_ATTENTE"].includes(r.status)).length,
      confirmed: rs.filter((r) => r.status === "RENDEZ_VOUS_CONFIRME").length,
      arrived: rs.filter((r) => r.status === "PATIENT_ARRIVE").length,
    };
  }, [data.requests]);

  const openDetail = async (id) => {
    try { const { data: d } = await api.get(`/requests/${id}`); setDetail(d); setNote(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/requests/${id}/status`, { status }); toast.success("Statut mis à jour"); await load(); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/requests/${detail.id}/notes`, { text: note.trim() }); setNote(""); openDetail(detail.id); toast.success("Note ajoutée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resendSms = async (id) => {
    try { const { data: d } = await api.post(`/requests/${id}/sms`); toast.success(d.provider === "twilio" ? "SMS envoyé" : "SMS simulé (journalisé)"); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteRequest = async (id) => {
    if (!(await confirm({ title: "Supprimer la demande", description: "Supprimer définitivement cette demande/réservation ? Le créneau de cabine sera libéré. Action irréversible.", confirmText: "Supprimer", destructive: true }))) return;
    try { await api.delete(`/requests/${id}`); toast.success("Demande supprimée."); if (detail?.id === id) setDetail(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const purgeAll = async () => {
    if (!(await confirm({ title: "Tout supprimer", description: "Supprimer TOUTES les demandes/réservations ? Les créneaux de cabine seront libérés. Action irréversible.", confirmText: "Tout supprimer", destructive: true }))) return;
    try { const { data: d } = await api.post("/requests/purge-all"); toast.success(`${d.deleted} demande(s) supprimée(s).`); setDetail(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Tableau de bord</h1>
            <p className="text-sm text-muted-foreground">Suivi des demandes patients et de la cabine de téléconsultation.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={purgeAll} data-testid="purge-all-btn" className="rounded-xl gap-2 h-11 text-rose-600 border-rose-200 hover:bg-rose-50 dark:border-rose-900"><Trash2 className="h-4 w-4" /> Tout supprimer</Button>
            <Button variant="outline" onClick={load} data-testid="refresh-btn" className="rounded-xl gap-2 h-11"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={ClipboardList} label="Demandes" value={stats.total} testid="stat-total" />
          <StatCard icon={Clock} label="En attente" value={stats.waiting} testid="stat-waiting" />
          <StatCard icon={CheckCircle2} label="RDV confirmés" value={stats.confirmed} testid="stat-confirmed" />
          <StatCard icon={LogIn} label="Patients arrivés" value={stats.arrived} testid="stat-arrived" />
        </div>

        <Card className="rounded-2xl p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Date d'arrivée</Label>
              <Input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="filter-date" className="h-11 rounded-xl" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Statut</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="filter-status" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>
                  {(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Recherche</Label>
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" placeholder="Nom ou code PH-…" className="h-11 pl-9 rounded-xl" /></div></div>
          </div>
          {fDate && <Button variant="ghost" size="sm" onClick={() => setFDate("")} className="mt-2 h-8 rounded-lg text-primary" data-testid="clear-date">Voir toutes les dates</Button>}
        </Card>

        {loading ? <p className="text-muted-foreground">Chargement…</p> :
          rows.length === 0 ? <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="empty-state">Aucune demande pour ces filtres.</Card> :
          <div className="space-y-3" data-testid="requests-list">
            {rows.map((r) => (
              <Card key={r.id} className="rounded-2xl p-4 flex flex-wrap items-center gap-4 hover:shadow-md transition-shadow" data-testid={`request-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold">{r.patientFirstName || "—"} {r.patientLastName || ""}</span>
                    <Badge variant="outline" className="font-mono text-xs">{r.publicCode}</Badge>
                    <Badge className={`${statusStyle(r.status)} border-0`} data-testid={`status-badge-${r.id}`}>{statusLabel(r.status)}</Badge>
                    {r.isTest && <Badge className="bg-amber-100 text-amber-800 border-0 dark:bg-amber-950 dark:text-amber-300" data-testid={`test-badge-${r.id}`}>Mode test</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Téléconsultation · RDV médical : <b className="text-foreground">{r.partnerAppointmentStart ? dtf(r.partnerAppointmentStart) : "non renseigné"}</b>
                    {r.booking && <span> · Cabine {hm(r.booking.startAt)}–{hm(r.booking.endAt)}</span>}
                    {r.preventionStatus === "fait" && <span className="ml-2 text-primary">· Bilan fait</span>}
                    {r.accompanimentRequested && <span className="ml-2 text-secondary">· Accompagnement demandé</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "RENDEZ_VOUS_CONFIRME" && <Button size="sm" onClick={() => changeStatus(r.id, "PATIENT_ARRIVE")} data-testid={`arrival-btn-${r.id}`} className="rounded-xl gap-1.5 h-10"><LogIn className="h-4 w-4" /> Arrivée</Button>}
                  {r.status === "PATIENT_ARRIVE" && <Button size="sm" variant="secondary" onClick={() => changeStatus(r.id, "TELECONSULTATION_TERMINEE")} data-testid={`done-btn-${r.id}`} className="rounded-xl gap-1.5 h-10"><CheckCircle2 className="h-4 w-4" /> Terminer</Button>}
                  <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`detail-btn-${r.id}`} className="rounded-xl h-10">Détails</Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteRequest(r.id)} data-testid={`delete-request-${r.id}`} className="rounded-xl h-10 w-10 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>}
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="detail-dialog">
          {detail && <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
                {detail.patientFirstName} {detail.patientLastName}
                <Badge variant="outline" className="font-mono text-xs">{detail.publicCode}</Badge>
                {detail.isTest && <Badge className="bg-amber-100 text-amber-800 border-0 dark:bg-amber-950 dark:text-amber-300" data-testid="detail-test-badge">Mode test</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="rounded-xl bg-muted/50 p-3 space-y-1">
                <p>Rendez-vous médical : <b>{detail.partnerAppointmentStart ? dtf(detail.partnerAppointmentStart) : "—"}</b></p>
                <p>Durée estimée : <b>{detail.partnerAppointmentDurationMinutes ? `${detail.partnerAppointmentDurationMinutes} min` : "—"}</b></p>
                <p>Arrivée demandée : <b>{detail.booking ? dtf(detail.booking.startAt) : "—"}</b></p>
                <p>Cabine réservée : <b>{detail.cabinName || "—"}{detail.booking ? ` (${hm(detail.booking.startAt)} → ${hm(detail.booking.endAt)})` : ""}</b></p>
                <p>Plateforme : <b>{detail.partnerId || "—"}</b></p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Modifier le statut</Label>
                <Select value={detail.status} onValueChange={(v) => changeStatus(detail.id, v)}>
                  <SelectTrigger data-testid="detail-status-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {detail.prevention && <div>
                <p className="font-semibold mb-1">Bilan prévention</p>
                <div className="rounded-xl border p-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <span>Âge : <b>{detail.prevention.ageRange || "—"}</b></span>
                  <span>Médecin traitant : <b>{detail.prevention.hasPrimaryDoctor ? "oui" : "non"}</b></span>
                  <span>Hypertension : <b>{detail.prevention.hypertension ? "oui" : "non"}</b></span>
                  <span>Diabète : <b>{detail.prevention.diabetes ? "oui" : "non"}</b></span>
                  <span>Tabac : <b>{detail.prevention.smoking ? "oui" : "non"}</b></span>
                  <span>Stress : <b>{detail.prevention.stress ? "oui" : "non"}</b></span>
                </div>
              </div>}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold">SMS</p>
                  <Button size="sm" variant="outline" onClick={() => resendSms(detail.id)} data-testid="resend-sms-btn" className="rounded-lg gap-1.5 h-8"><MessageSquare className="h-3.5 w-3.5" /> Renvoyer</Button>
                </div>
                {(detail.sms || []).length === 0 ? <p className="text-xs text-muted-foreground">Aucun SMS envoyé.</p> :
                  <div className="space-y-1.5">{detail.sms.map((m) => (
                    <div key={m.id} className="rounded-lg border p-2 text-xs">
                      <div className="flex justify-between"><span className="uppercase text-muted-foreground">{m.provider} · {m.status}</span><span className="text-muted-foreground">{hm(m.createdAt)}</span></div>
                      <p className="mt-0.5">{m.body}</p>
                    </div>))}</div>}
              </div>

              <div>
                <p className="font-semibold mb-1">Notes internes</p>
                {(detail.notes || []).map((n) => <div key={n.id} className="rounded-lg bg-muted/50 p-2 text-xs mb-1.5">{n.text} <span className="text-muted-foreground">· {hm(n.createdAt)}</span></div>)}
                <div className="flex gap-2 mt-1">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Ajouter une note…" className="rounded-xl min-h-[44px]" rows={1} />
                  <Button onClick={addNote} data-testid="add-note-btn" className="rounded-xl h-11">Ajouter</Button>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-1">Historique</p>
                <div className="space-y-1">{(detail.history || []).map((h) => (
                  <div key={h.id} className="text-xs text-muted-foreground flex justify-between gap-2">
                    <span>{statusLabel(h.toStatus)} <span className="opacity-60">({h.changedBy})</span></span>
                    <span>{hm(h.createdAt)}</span>
                  </div>))}</div>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
